// Sample storage in IndexedDB. Survives page reloads and isn't capped at the
// 5 MB localStorage limit, so it can hold real audio (samples are typically
// 100 KB – 5 MB each).
//
// Keys are namespaced strings like "drum:kick" or "sampler:c4" so the
// hydration step on app boot can route each blob to the right instrument.

const DB_NAME = 'music-maker';
const STORE = 'samples';
const VERSION = 1;

let dbPromise = null;
let supported = typeof indexedDB !== 'undefined';

function getDB() {
    if (!supported) return Promise.reject(new Error('IndexedDB unavailable'));
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open(DB_NAME, VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
            req.onblocked = () => reject(new Error('IndexedDB blocked'));
        });
    }
    return dbPromise;
}

export async function putSample(key, blob, name) {
    if (!supported) return;
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put({ blob, name, savedAt: Date.now() }, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function getSample(key) {
    if (!supported) return null;
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
    });
}

export async function deleteSample(key) {
    if (!supported) return;
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function listSamples() {
    if (!supported) return [];
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const out = [];
        const cursor = db.transaction(STORE, 'readonly').objectStore(STORE).openCursor();
        cursor.onsuccess = (e) => {
            const c = e.target.result;
            if (!c) { resolve(out); return; }
            out.push({ key: c.key, name: c.value.name });
            c.continue();
        };
        cursor.onerror = () => reject(cursor.error);
    });
}
